/**
 * The range of an absence request as the original form drew it (TF-PRESERVATION-FINAL A02).
 * `absence_types.type` decides the shape: 'date' is the full-day flow, 'time' is one date with
 * a Fra and Til clock, 'datetime' is a start and an end each with date and clock. Anything else
 * is read as a full day, the way the payroll does. Nothing here converts a clock to an instant:
 * the API is sent the company's wall clock and resolves it in Europe/Oslo, refusing a clock the
 * day lacks or repeats, so no time is guessed on this side.
 */
import { ApiError, errorText } from '@/api/client';
import type { ApiAbsence, ApiAbsenceType } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { DateInput } from '@/ui/DateInput';
import { fmtD } from '@/ui/format';
import { useState } from 'react';

export type Kind = 'date' | 'time' | 'datetime';
export const kindOf = (type: string | null | undefined): Kind =>
  type === 'time' || type === 'datetime' ? type : 'date';
/** The type comes from the types list when it is there; the request's own row carries `type` too, only the shared interface omits it. */
export const kindOfAbsence = (a: ApiAbsence, types: Pick<ApiAbsenceType, 'id' | 'type'>[]): Kind =>
  kindOf(
    types.find((t) => t.id === a.absence_type?.id)?.type ??
      (a.absence_type as { type?: string } | null)?.type,
  );

export interface Range {
  from: string;
  fromT: string;
  to: string;
  toT: string;
}

/** The stored instants as Oslo dates and clocks. */
export function rangeOf(a: Pick<ApiAbsence, 'start_date' | 'end_date'>): Range {
  const s = oslo(a.start_date);
  const e = oslo(a.end_date);
  return { from: s?.date ?? '', fromT: s?.time ?? '', to: e?.date ?? s?.date ?? '', toT: e?.time ?? '' };
}
export const sameRange = (a: Range, b: Range, kind: Kind): boolean =>
  a.from === b.from && a.to === b.to && (kind === 'date' || (a.fromT === b.fromT && a.toT === b.toT));

/**
 * What the API is sent, or why not. Dates go as 'YYYY-MM-DD', clocks as 'YYYY-MM-DDTHH:mm' with no
 * offset. The end must follow the start; a Til clock at or before Fra on the same date is refused
 * and, for the one-date type, asks for the end date instead of assuming the next day.
 */
/** `field` names the input the problem is about, so the form can mark and focus it (#272 B-38). */
export function bodyOf(
  r: Range,
  kind: Kind,
): { start_date: string; end_date: string } | { problem: string; field: keyof Range } {
  if (!r.from) return { problem: T('Oppgi dato.'), field: 'from' };
  const to = r.to || r.from;
  if (kind === 'date') {
    if (to < r.from) return { problem: T('Til kan ikke være før Fra.'), field: 'to' };
    return { start_date: r.from, end_date: to };
  }
  if (!r.fromT || !r.toT)
    return {
      problem: T('Oppgi klokkeslett fra og til.'),
      field: r.fromT ? 'toT' : 'fromT',
    };
  const a = `${r.from}T${r.fromT}`;
  const b = `${to}T${r.toT}`;
  if (b <= a) {
    return {
      field: 'toT',
      problem:
        kind === 'time' && to === r.from
          ? T('Til må være etter Fra. Går fraværet over midnatt, oppgi sluttdato.')
          : T('Til må være etter Fra.'),
    };
  }
  return { start_date: a, end_date: b };
}

/** '5.10.2026 til 7.10.2026', '5.10.2026 10:00 til 12:00', '5.10.2026 22:00 til 6.10.2026 06:00'. */
export function describe(r: Range, kind: Kind): string {
  if (!r.from) return '';
  const to = r.to || r.from;
  if (kind === 'date')
    return to !== r.from ? T('{a} til {b}', { a: fmtD(r.from), b: fmtD(to) }) : fmtD(r.from);
  if (to === r.from) return T('{a} til {b}', { a: `${fmtD(r.from)} ${r.fromT}`, b: r.toT });
  return T('{a} til {b}', { a: `${fmtD(r.from)} ${r.fromT}`, b: `${fmtD(to)} ${r.toT}` });
}

/** The API's refusal in the reader's words; anything unrecognised is shown as the API said it. */
const SERVER: [RegExp, string][] = [
  [
    /clock does not exist: (.+)$/,
    'Klokkeslettet {at} finnes ikke den dagen (overgang til sommertid). Velg et annet.',
  ],
  [
    /clock happens twice: (.+)$/,
    'Klokkeslettet {at} finnes to ganger den dagen (overgang til vintertid). Velg et annet.',
  ],
  [/end_date must (be after|not be before) start_date/, 'Til må være etter Fra.'],
  [/absence type is not active or does not exist/, 'Fraværstypen finnes ikke lenger. Velg en annen.'],
];
export function explain(e: unknown): string {
  // a refusal with a code carries the server's Norwegian sentence; the table is for anything older
  if (e instanceof ApiError && e.code) return e.message;
  // the request never reached the server: Norwegian, not the browser's «Failed to fetch» (#272 B-28, B-38)
  if (e instanceof TypeError) return errorText(e);
  const msg = e instanceof Error ? e.message : String(e);
  for (const [re, text] of SERVER) {
    const m = re.exec(msg);
    if (m) return T(text, { at: m[1] ?? '' });
  }
  return msg;
}

/**
 * The inputs for one kind. Labels stay 'Fra' and 'Til' for the date flow and the clocks, as before;
 * the one-date type reveals a Sluttdato only when asked, so an overnight end is typed, not inferred.
 */
type FieldsProps = {
  kind: Kind;
  value: Range;
  onChange: (r: Range) => void;
  /** the field a refusal is about, marked aria-invalid and pointed at the message `errId` */
  invalid?: keyof Range | undefined;
  errId?: string | undefined;
};
/** A new kind starts on one day again: the key remounts the fields, so the Sluttdato toggle of a previous kind cannot carry an end date over. */
export function RangeFields(props: FieldsProps) {
  return <Fields key={props.kind} {...props} />;
}
function Fields({ kind, value, onChange, invalid, errId }: FieldsProps) {
  const [endDay, setEndDay] = useState(false);
  const set = (p: Partial<Range>) => onChange({ ...value, ...p });
  const inp = (
    type: 'date' | 'time',
    label: string,
    key: keyof Range,
    extra?: (v: string) => Partial<Range>,
  ) =>
    type === 'date' ? (
      <DateInput
        className="inp"
        aria-label={label}
        aria-invalid={invalid === key || undefined}
        aria-describedby={invalid === key ? errId : undefined}
        value={value[key]}
        onValueChange={(v) => set({ [key]: v, ...(extra ? extra(v) : {}) })}
      />
    ) : (
      <input
        className="inp"
        type={type}
        aria-label={label}
        aria-invalid={invalid === key || undefined}
        aria-describedby={invalid === key ? errId : undefined}
        value={value[key]}
        onChange={(e) => set({ [key]: e.target.value, ...(extra ? extra(e.target.value) : {}) })}
      />
    );
  if (kind === 'date') {
    // each date on its own row, at the field's full width: side by side in a docked drawer each was
    // about 110 px and «08.10.2026» was cut to «08.10.2C» (#154 VF-10)
    return (
      <>
        <div className="fld">
          <span className="lbl">{T('Fra')}</span>
          {inp('date', T('Fra'), 'from')}
        </div>
        <div className="fld">
          <span className="lbl">{T('Til')}</span>
          {inp('date', T('Til'), 'to')}
        </div>
      </>
    );
  }
  if (kind === 'time') {
    const showEnd = endDay || (!!value.to && value.to !== value.from);
    return (
      <>
        <div className="fld">
          <span className="lbl">{T('Dato')}</span>
          {/* the end date follows the date until it has been set apart */}
          {inp('date', T('Dato'), 'from', (v) => (showEnd ? {} : { to: v }))}
        </div>
        <div className="fld">
          <span className="lbl">{T('Fra / til')}</span>
          <div className="two">
            {inp('time', T('Fra'), 'fromT')}
            {inp('time', T('Til'), 'toT')}
          </div>
        </div>
        {showEnd ? (
          <div className="fld">
            <span className="lbl">{T('Sluttdato')}</span>
            {inp('date', T('Sluttdato'), 'to')}
          </div>
        ) : (
          <button
            type="button"
            style={{
              margin: '0 0 10px',
              padding: 0,
              background: 'none',
              border: 0,
              color: 'var(--act)',
              font: 'inherit',
              cursor: 'pointer',
            }}
            onClick={() => setEndDay(true)}
          >
            {T('Slutter en annen dag')}
          </button>
        )}
      </>
    );
  }
  return (
    <>
      <div className="fld">
        <span className="lbl">{T('Fra')}</span>
        <div className="two">
          {inp('date', T('Fra dato'), 'from')}
          {inp('time', T('Fra klokkeslett'), 'fromT')}
        </div>
      </div>
      <div className="fld">
        <span className="lbl">{T('Til')}</span>
        <div className="two">
          {inp('date', T('Til dato'), 'to')}
          {inp('time', T('Til klokkeslett'), 'toT')}
        </div>
      </div>
    </>
  );
}
